import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';
import { useApolloClient } from '@apollo/client';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import Articles from './Components/Articles';
import Button from './Components/TableButton';
import Payment from './Components/Payment';
import { useI18n, useStaffCan, useCurrency, useSelector, useDispatch, useButton } from '~Hooks';
import Messages from './Components/Messages';
import ServiceCharge from './Components/ServiceCharge';
import { filterOutServiceCharge } from '~Utils/serviceCharge';
import { useStorage } from '~Components/Storage';
import compressIcon from '~Assets/icons/compress.svg';
import expandIcon from '~Assets/icons/expand.svg';
import Icon from '~ComponentsImproved/Icon';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import { CashDrawerOpenInfo } from '~Scenes/POS/Components/Cart';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box, RedButton } from '~Styles';
import RemoveItemModal from './Components/RemoveItemModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Modal from '~ComponentsImproved/Modal';
import ItemModifications from '~Scenes/POS/Components/ItemModifications';
import { ItemFragmentOnItem } from '~Scenes/POS/Components/GridButtons/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Components/GridButtons/queries';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { shouldShowModificationPopup } from '~Hooks/ItemModifications/utils';
import useChequeItemQuantity, { AddedItemEvent } from './Hooks/useChequeItemQuantity';
import QuantityStepper from '~ComponentsImproved/TSR/QuantityStepper';

const Wrapper = styled.div<{ isParkedOrder: boolean }>`
  background-color: ${({ isParkedOrder }) =>
    isParkedOrder ? 'var(--colorBlueGray1100)' : 'var(--colorBlueGray1300)'};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  padding: 0 0 16px 0;
  display: flex;
  flex-flow: column nowrap;
  height: calc(100% - 48px);
  box-sizing: border-box;
`;

const ItemsWrapper = styled.div`
  overflow: auto;
  position: relative;
  padding-bottom: 80px;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-flow: column nowrap;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HeaderBox = styled(Box)<{ isParkedOrder?: boolean }>`
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid var(--colorBlueGray1100);
  color: ${({ isParkedOrder }) =>
    isParkedOrder ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray800)'};
`;

const Qty = styled.div`
  width: 32px;
  margin-right: 8px;
`;

const BottomWrapper = styled.div<{ isParkedOrder: boolean }>`
  border-top: 1px solid var(--colorBlueGray1000);
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;

  ${({ isParkedOrder }) =>
    `
    &:after {
      content: '';
      background: linear-gradient(to top, 
        ${isParkedOrder ? 'var(--colorBlueGray1100)' : 'rgba(14, 18, 22, 1)'} 0%, 
        ${isParkedOrder ? 'transparent' : 'rgba(14, 18, 22, 0)'} 100%
      );
      height: 60px;
      width: 100%;
      position: absolute;
      top: -60px;
      right: 0;
      pointer-events: none;
    }
  `}
`;

const BottomSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  position: relative;
  padding: 16px 16px 0;
  max-height: 50vh;
`;

const PriceInfo = styled.div`
  margin-bottom: 8px;
`;

const PayText = styled.p`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  margin-bottom: 4px;
`;

const DeleteButtonWrapper = styled.div`
  margin-top: auto;
  margin-bottom: 196px;
  padding: 16px 16px 0;
`;

const CompressButton = styled.div`
  padding: 4px;
  background: var(--colorBlueGray1200);
  border-radius: 2px;
`;

const ActionButtonsWrapper = styled.div`
  box-sizing: border-box;
  position: absolute;
  width: calc(100% - 32px);
  transform: translateY(-100%);
  z-index: 1;
  top: -4px;
  margin: 0 16px 16px;
`;

interface Props {
  tableId: string;
  tableName: string;
  selectedCheque: string | null;
  isBongVisible?: boolean;
  isParkedOrder?: boolean; // If the bill is a parked order we disable actions
  setSelectedCheque?: (id: string | null) => void;
  homelessItems?: boolean;
  showLogin?: () => void;
  onDelete?: () => void;
}

const Bill: React.FunctionComponent<Props> = ({
  tableId,
  tableName,
  selectedCheque,
  isBongVisible,
  isParkedOrder,
  setSelectedCheque,
  homelessItems,
  showLogin,
  onDelete,
}) => {
  const { i18n } = useI18n();
  const cashDrawer = useCashDrawer();
  const api = useTableApi();
  const client = useApolloClient();
  const universe = useTableUniverse();
  const currency = useCurrency();
  const [storage, setStorage] = useStorage();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [showRemoveChequeButton, setShowRemoveChequeButton] = useState(false);
  // A fresh object every time an item is added, so the same product id can
  // retrigger the animation even if it was already pulsing/flashing
  const [addedItemEvent, setAddedItemEvent] = useState<AddedItemEvent | null>(null);
  const [duplicateModal, setDuplicateModal] = useState<{
    sourceItem: typeof universe.items[number];
    menuItem: ItemFragmentOnItem;
    count: number;
  } | null>(null);

  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );

  const cheque = universe.cheques.find((c) => c.id === selectedCheque);
  const chequeItems = universe.items.filter((c) =>
    homelessItems ? !c.chequeId : c.chequeId === selectedCheque,
  );
  const nonSelectedItems = chequeItems.filter((item) => !selectedItems.includes(item.id));
  const nonSelectedItemsTotalSum = nonSelectedItems.reduce((acc, item) => {
    return (acc += item.amount - item.discount);
  }, 0);

  // Unselect items when selecting another cheque. Also clear any pending
  // add-animation - otherwise it survives here (this component doesn't
  // remount just because `selectedCheque` changes) and replays on
  // ArticleItem's next mount, e.g. when navigating back into this cheque.
  useEffect(() => {
    if (selectedCheque) {
      setSelectedItems([]);
      setAddedItemEvent(null);
    }
  }, [selectedCheque]);

  // Wait with showing the remove cheque button until the user has had time to add items
  // to the cheque. If no items are added after 5 seconds, we show the button.
  useEffect(() => {
    setShowRemoveChequeButton(false);

    if (cheque && chequeItems.length === 0 && !isBongVisible) {
      const timeout = setTimeout(() => {
        setShowRemoveChequeButton(true);
      }, 5000);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [cheque?.id, chequeItems.length, isBongVisible]);

  const staffCan = useStaffCan();
  const staffCanMutate = cheque && staffCan.mutateCheque(cheque.id);

  const itemsWithoutServiceCharge = filterOutServiceCharge(chequeItems);

  const chequeMessages = universe.messages.filter((m) => m.chequeId === selectedCheque);

  /**
   * Deletes the selected cheque. Only available if the cheque is empty.
   */
  const handleDelete = useCallback(() => {
    api.removeCheque(selectedCheque!, cheque?.tableId || '');
    api.log.removeCheque(selectedCheque!, '--');

    if (onDelete) {
      onDelete();
    }

    if (setSelectedCheque) {
      setSelectedCheque(null);
    }
  }, [selectedCheque, setSelectedCheque]);

  const total = cheque ? cheque.amount - cheque.discount : 0;
  const leftToPay = cheque ? total - cheque.paidAmount : 0;

  const removeButton = useButton({
    onClick: () => setShowRemoveModal(true),
    // The remaining amount on the cheque (non selected items) must be more than the paid amount to allow removal
    disabled: cheque
      ? cheque.paidAmount > 0 && nonSelectedItemsTotalSum <= cheque.paidAmount
      : false,
    disabledToast: i18n('TM.CantRemoveItemsWithPayments'),
  });

  // Look up the menu item of the selection so we know its modification groups
  const selectedSourceItem = chequeItems.find((item) => selectedItems.includes(item.id)) ?? null;

  const selectedMenuItem = useMemo<ItemFragmentOnItem | null>(() => {
    if (!selectedSourceItem) {
      return null;
    }

    try {
      return client.readFragment<ItemFragmentOnItem>({
        id: `Item${selectedSourceItem.itemId}`,
        fragment: ITEM_FRAGMENT,
      });
    } catch (e) {
      return null;
    }
  }, [selectedSourceItem?.itemId]);

  const itemModifications = useItemModifications(selectedMenuItem?.modificationGroups ?? []);

  // Same rule as opening the order screen (ChequePanel/Footer): don't allow
  // adding more items once a payment has been made on the cheque
  const canAddCopies = !isParkedOrder && !cheque?.paidAmount;

  const quantity = useChequeItemQuantity({
    chequeId: selectedCheque,
    tableId,
    tableName,
    items: chequeItems,
    selectedItemIds: selectedItems,
    setSelectedItemIds: setSelectedItems,
    setAddedItemEvent,
    compressedCart: storage.compressedCart,
    canAdd: canAddCopies,
    canRemove: staffCan.REFUND && !isParkedOrder && !removeButton.disabled,
    // Let staff pick variations (pre-selected to match the item being copied)
    // instead of blindly copying them, either when the product would normally
    // show the popup on add, or when the item being copied was configured with
    // any modifications - added variations OR removed default ingredients
    requiresModifications: (sourceItem) =>
      Boolean(selectedMenuItem) &&
      (shouldShowModificationPopup(itemModifications.modificationGroups) ||
        sourceItem.richVariations.length > 0 ||
        sourceItem.removedVariations.length > 0),
    onRequestModifications: (sourceItem, count) =>
      setDuplicateModal({ sourceItem, menuItem: selectedMenuItem!, count }),
  });

  /** The lines the remove modal acts on: a confirmed "Remove N", or the whole selection */
  const itemsToRemove = quantity.pendingRemoveIds.length
    ? quantity.pendingRemoveIds
    : selectedItems;

  const removeChequeButton = useButton({
    onClick: handleDelete,
  });

  return (
    <Wrapper isParkedOrder={isParkedOrder || false}>
      {homelessItems && !cheque && showLogin && (
        <ItemsWrapper>
          <Articles
            items={itemsWithoutServiceCharge}
            staffId={staffId}
            showLogin={showLogin}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </ItemsWrapper>
      )}

      {cheque && (
        <>
          <HeaderBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            isParkedOrder={isParkedOrder || false}>
            <Box display="flex" alignItems="center">
              <Qty>
                <Heading level={5} color="var(--colorBlueGray600)">
                  {i18n('TM.Qty')}
                </Heading>
              </Qty>
              <Heading level={5} color="var(--colorBlueGray600)">
                {i18n('TM.Article')}
              </Heading>
            </Box>
            <CompressButton
              onTouchStart={() => {
                setStorage({
                  ...storage,
                  compressedCart: !storage.compressedCart,
                });
              }}>
              <Icon
                src={storage.compressedCart ? expandIcon : compressIcon}
                color="var(--colorBlueGray100)"
                width="20px"
                height="20px"
              />
            </CompressButton>
          </HeaderBox>
          <ItemsWrapper>
            <Articles
              items={itemsWithoutServiceCharge}
              staffId={staffId}
              showLogin={showLogin}
              selectedItems={selectedItems}
              setSelectedItems={(selection) => {
                if (isParkedOrder) {
                  // We don't allow modification of an order when it's parked
                  return;
                }

                setSelectedItems(selection);
              }}
              hasPartialPayment={cheque.paidAmount > 0}
              addedItemEvent={addedItemEvent}
            />
            {chequeMessages.length > 0 && <Messages messages={chequeMessages} />}
            {cheque.serviceCharge && (
              <ServiceCharge
                chequeId={cheque.id}
                percentage={cheque.serviceCharge.percentage}
                disableTouchAction={isParkedOrder}
              />
            )}
          </ItemsWrapper>

          <BottomWrapper isParkedOrder={isParkedOrder || false}>
            {selectedItems.length > 0 && (
              <>
                <ActionButtonsWrapper>
                  <Box display="flex" flexDirection="column" gap="xs">
                    {quantity.canAdjust && staffCanMutate && (
                      <QuantityStepper
                        pending={quantity.pending}
                        canIncrement={quantity.canIncrement}
                        canDecrement={quantity.canDecrement}
                        onIncrement={quantity.increment}
                        onDecrement={quantity.decrement}
                        onConfirm={quantity.confirm}
                        incrementDisabledToast={i18n('TOAST.DisableOrderingAfterPayment')}
                        decrementDisabledToast={i18n('TM.CantRemoveItemsWithPayments')}
                      />
                    )}
                    {staffCan.REFUND && (
                      <RedButton {...removeButton} fluid size="small">
                        {i18n('TM.Remove')}
                      </RedButton>
                    )}
                  </Box>
                </ActionButtonsWrapper>
                {(showRemoveModal || quantity.pendingRemoveIds.length > 0) && (
                  <RemoveItemModal
                    selectedItems={itemsToRemove}
                    tableId={tableId}
                    tableName={tableName}
                    clearChosen={() => {
                      setSelectedItems([]);
                      setShowRemoveModal(false);
                      quantity.clearPendingRemove();
                      if (onDelete) {
                        onDelete();
                      }
                    }}
                    closeModal={() => {
                      setShowRemoveModal(false);
                      quantity.clearPendingRemove();
                    }}
                  />
                )}
              </>
            )}

            {chequeItems.length > 0 ? (
              <BottomSection>
                <div>
                  <PriceInfo>
                    {cheque?.serviceCharge && (
                      <PayText>
                        <span>
                          {i18n('ServiceCharge.Title')} {cheque.serviceCharge.percentage}%
                        </span>
                        {currency.format(cheque.serviceCharge.amount)}
                      </PayText>
                    )}
                    <PayText>
                      <span>{i18n('TM.Discount')}</span>
                      {currency.format(cheque.discount)}
                    </PayText>
                    {cheque.paidAmount > 0 && (
                      <PayText>
                        <span>{i18n('TM.Payed')}</span>
                        {currency.format(cheque.paidAmount)}
                      </PayText>
                    )}
                    <PayText>
                      <span>{i18n('TM.Total')}</span>
                      {currency.format(leftToPay)}
                    </PayText>
                  </PriceInfo>
                  {cashDrawer.isOpen ? (
                    <CashDrawerOpenInfo>
                      <Paragraph level={3} color="var(--colorBlueGray1200)">
                        {i18n('Cashdrawer.BlockedPayments')}
                      </Paragraph>
                    </CashDrawerOpenInfo>
                  ) : (
                    staffCanMutate &&
                    showLogin && (
                      <Payment
                        tableName={tableName}
                        tableId={tableId}
                        selectedCheque={selectedCheque}
                        selectedItems={selectedItems}
                        clearSelectedItems={() => setSelectedItems([])}
                        hasPartialPayment={cheque.paidAmount > 0}
                      />
                    )
                  )}
                </div>
              </BottomSection>
            ) : (
              <DeleteButtonWrapper>
                {staffCan.REFUND && staffCanMutate && showRemoveChequeButton && (
                  <RedButton fluid size="large" {...removeChequeButton}>
                    {i18n('TM.RemoveCheque')}
                  </RedButton>
                )}
              </DeleteButtonWrapper>
            )}
          </BottomWrapper>
        </>
      )}

      <Modal isShowing={!!duplicateModal} backdropClose={false}>
        <ItemModifications
          item={duplicateModal?.menuItem ?? null}
          onClose={() => setDuplicateModal(null)}
          prevSelected={
            duplicateModal
              ? {
                  variations: duplicateModal.sourceItem.richVariations,
                  removedVariations: duplicateModal.sourceItem.removedVariations,
                  upsales: [],
                }
              : undefined
          }
          addItem={({ id, variations = [], removedVariations = [] }) => {
            // Only the main item's modifications are handled here (id is
            // undefined for it); upsells/as-item variations are ignored
            if (duplicateModal && id === undefined) {
              quantity.duplicate(
                duplicateModal.sourceItem,
                duplicateModal.count,
                variations,
                removedVariations,
              );
            }

            setDuplicateModal(null);
            return { cartItemIds: [] };
          }}
        />
      </Modal>
    </Wrapper>
  );
};

export default React.memo(Bill);
